import React from 'react';
import styled from 'styled-components';

export type StatusBadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary' | 'pending' | 'processing' | 'in-transit' | 'delivered' | 'delayed' | 'locked' | 'ready' | 'optional' | 'approved' | 'filed' | 'blocked' | 'paid' | 'partial' | 'overdue' | 'out-for-delivery';
export type StatusBadgeSize = 'sm' | 'md' | 'lg';

export interface StatusBadgeProps {
  variant: StatusBadgeVariant;
  size?: StatusBadgeSize;
  label: string;
  dot?: boolean;
  pulse?: boolean;
  className?: string;
}

const BadgeContainer = styled.div<{ 
  $variant: StatusBadgeVariant; 
  $size: StatusBadgeSize;
  $pulse: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme, $size }) => {
    if ($size === 'sm') return theme.spacing[1];
    if ($size === 'lg') return theme.spacing[3];
    return theme.spacing[2];
  }};
  padding: ${({ theme, $size }) => {
    if ($size === 'sm') return `${theme.spacing[1]} ${theme.spacing[2]}`;
    if ($size === 'lg') return `${theme.spacing[3]} ${theme.spacing[5]}`;
    return `${theme.spacing[1]} ${theme.spacing[2]}`;
  }};
  background: ${({ theme, $variant }) => {
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    switch ($variant) {
      // Primary variants
      case 'success':
      case 'delivered':
      case 'approved':
      case 'paid':
      case 'ready':
        return hexToRgba(theme.colors.success, 0.1);
      case 'warning':
      case 'partial':
      case 'optional':
      case 'processing':
        return hexToRgba(theme.colors.warning, 0.1);
      case 'error':
      case 'overdue':
      case 'delayed':
      case 'blocked':
        return hexToRgba(theme.colors.error, 0.1);
      case 'info':
      case 'in-transit':
        return hexToRgba(theme.colors.info, 0.1);
      case 'primary':
      case 'pending':
        return hexToRgba(theme.colors.primary, 0.1);
      case 'out-for-delivery':
        return 'rgba(221, 214, 254, 1)'; // Purple: #ddd6fe
      case 'locked':
      case 'neutral':
      default:
        return theme.colors.gray100;
    }
  }};
  border: 1.25px solid ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
      case 'delivered':
      case 'approved':
      case 'paid':
      case 'ready':
        return theme.colors.success;
      case 'warning':
      case 'partial':
      case 'optional':
      case 'processing':
        return theme.colors.warning;
      case 'error':
      case 'overdue':
      case 'delayed':
      case 'blocked':
        return theme.colors.error;
      case 'info':
      case 'in-transit':
        return theme.colors.info;
      case 'primary':
      case 'pending':
        return theme.colors.primary;
      case 'out-for-delivery':
        return '#5b21b6'; // Purple border
      case 'locked':
      case 'neutral':
      default:
        return theme.colors.gray300;
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme, $size }) => {
    if ($size === 'sm') return theme.typography.fontSize.xs;
    if ($size === 'lg') return theme.typography.fontSize.md;
    return theme.typography.fontSize.sm;
  }};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  width: fit-content;
  
  ${({ $pulse }) => $pulse && `
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    
    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.7;
      }
    }
  `}
`;

const StatusDot = styled.div<{ $variant: StatusBadgeVariant; $size: StatusBadgeSize }>`
  width: ${({ $size }) => {
    if ($size === 'sm') return '6px';
    if ($size === 'lg') return '10px';
    return '8px';
  }};
  height: ${({ $size }) => {
    if ($size === 'sm') return '6px';
    if ($size === 'lg') return '10px';
    return '8px';
  }};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
      case 'delivered':
      case 'approved':
      case 'paid':
      case 'ready':
        return theme.colors.success;
      case 'warning':
      case 'partial':
      case 'optional':
      case 'processing':
        return theme.colors.warning;
      case 'error':
      case 'overdue':
      case 'delayed':
      case 'blocked':
        return theme.colors.error;
      case 'info':
      case 'in-transit':
        return theme.colors.info;
      case 'primary':
      case 'pending':
        return theme.colors.primary;
      case 'out-for-delivery':
        return '#5b21b6'; // Purple dot
      case 'locked':
      case 'neutral':
      default:
        return theme.colors.gray500;
    }
  }};
  flex-shrink: 0;
`;

const BadgeLabel = styled.span`
  white-space: nowrap;
`;

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  variant,
  size = 'md',
  label,
  dot = false,
  pulse = false,
  className,
}) => {
  return (
    <BadgeContainer 
      $variant={variant} 
      $size={size}
      $pulse={pulse}
      className={className}
    >
      {dot && <StatusDot $variant={variant} $size={size} />}
      <BadgeLabel>{label}</BadgeLabel>
    </BadgeContainer>
  );
};
